import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Info, Zap, ShieldCheck, Star, Search, Shield, Server, Users, DollarSign, TrendingUp, Calendar, Clock, Coffee } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Criar uma Rotina de Atendimento para Clientes IPTV", href: "/blog/como-criar-rotina-atendimento-clientes-iptv" }
  ];

  const sections = [
    {
      title: "O Equilíbrio entre Suporte e Saúde Mental",
      content: (
        <div className="prose prose-invert max-w-none space-y-6 text-lg text-muted-foreground">
          <p>
            Muitos revendedores master falham no primeiro ano não por falta de clientes, mas por burnout. Tentar responder cada mensagem no segundo que ela chega, 24 horas por dia, é a receita para o desastre. Em 2026, a <strong>rotina de atendimento</strong> é o que separa os amadores exaustos dos profissionais bem-sucedidos.
          </p>
          <p>
            Ter uma rotina não significa atender mal; significa atender com método. Quando você define horários e processos, o cliente sabe o que esperar e você ganha tempo para focar na estratégia de crescimento do seu negócio.
          </p>
          <div className="bg-electric/10 border-l-4 border-electric p-8 my-8 rounded-r-3xl">
            <h4 className="text-white font-black mb-2 uppercase tracking-tighter italic text-xl">Regra de Ouro</h4>
            <p className="italic text-white/90">
              "Um suporte excelente é aquele que resolve o problema, não aquele que responde rápido mas sem solução. Priorize a eficácia sobre a pressa."
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Passo a Passo para uma Rotina de Elite",
      type: "grid" as const,
      content: (
        <>
          {[
            { t: "Horários Definidos", d: "Estabeleça janelas claras de atendimento (Ex: 09h às 12h e 14h às 18h). Fora disso, use mensagens automáticas informativas.", i: <Clock /> },
            { t: "Blocos de Produtividade", d: "Reserve a primeira hora do dia para resolver renovações e a última para triagem de novos prospectos.", i: <Calendar /> },
            { t: "Triagem de Problemas", d: "Ensine o cliente a fornecer as informações básicas (dispositivo, erro, internet) antes mesmo de você atendê-lo.", i: <Search /> },
            { t: "Canais de Emergência", d: "Tenha uma forma do cliente sinalizar se o problema é 'queda total' ou apenas uma dúvida simples. Trate prioridades.", i: <Zap /> },
            { t: "Manuais Prontos", d: "Tenha uma biblioteca de áudios e vídeos tutoriais. 80% dos atendimentos podem ser resolvidos enviando um link.", i: <Info /> },
            { t: "Momentos de Descompressão", d: "Saia do computador. Um revendedor descansado toma decisões melhores e atende com mais paciência e educação.", i: <Coffee /> }
          ].map((item, i) => (
            <div key={i} className="p-8 rounded-[2.5rem] bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-electric/10 text-electric flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                {item.i}
              </div>
              <h3 className="text-2xl font-black tracking-tighter mb-4 italic uppercase">{item.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{item.d}</p>
            </div>
          ))}
        </>
      )
    },
    {
      title: "Utilizando a Estabilidade P2 PRO a seu Favor",
      content: (
        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-lg text-muted-foreground">
            A melhor rotina de suporte é aquela em que o telefone não toca. Ao escolher o sinal da <strong>P2 PRO</strong>, você reduz o suporte reativo em mais de 60% devido à estabilidade extrema do nosso sistema.
          </p>
          <div className="p-10 rounded-[3rem] bg-white/[0.02] border border-white/5 my-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-electric/5 blur-[80px] rounded-full" />
            <h4 className="text-2xl font-black mb-6 italic uppercase tracking-tighter flex items-center gap-3">
              <ShieldCheck className="text-electric w-8 h-8" /> Checklist do Atendimento Nota 10
            </h4>
            <ul className="space-y-4">
              <li className="flex gap-4 items-start">
                <Check className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Respostas Rápidas:</span> Use a função de respostas rápidas do WhatsApp Business para os FAQs.</p>
              </li>
              <li className="flex gap-4 items-start">
                <Check className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Status do Sistema:</span> Tenha um grupo ou canal onde você informa sobre atualizações de grade, evitando mensagens individuais.</p>
              </li>
              <li className="flex gap-4 items-start">
                <Check className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Pesquisa de Satisfação:</span> Pergunte ao cliente o que ele achou do atendimento após a resolução. O feedback constrói a rotina.</p>
              </li>
            </ul>
          </div>
          <p className="text-lg text-muted-foreground">
            Uma rotina profissional atrai clientes profissionais. No modelo pós-pago da P2 PRO, você foca em manter a qualidade desse atendimento e na retenção da sua base, garantindo que sua operação seja lucrativa e, acima de tudo, sustentável mentalmente para você.
          </p>
        </div>
      )
    }
  ];

  return (
    <SEOPage
      category="Dicas Painel IPTV POS PAGO"
      title="Como Criar uma Rotina de Atendimento para Clientes IPTV"
      subtitle="Organize seus horários, ferramentas e processos para entregar um suporte de elite sem abrir mão da sua qualidade de vida e produtividade."
      heroImage="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200"
      introParagraphs={[
        "Muitos revendedores de IPTV vivem 'presos' ao celular, respondendo mensagens até no jantar ou durante o sono. Se você quer que sua revenda se torne um negócio de verdade, você precisa de uma rotina de atendimento. O amadorismo de estar online 24h sem processos destrói sua saúde e seu negócio.",
        "Neste artigo, vamos ensinar como estruturar seu dia de trabalho para ser altamente produtivo. Vamos falar sobre automação de respostas, triagem de suporte e como educar o seu cliente para que ele respeite seus horários de atendimento.",
        "Com o suporte da P2 PRO e as técnicas corretas de gestão de tempo, você verá que é possível gerenciar centenas de clientes e ainda ter tempo livre para o que realmente importa. Vamos profissionalizar seu atendimento hoje."
      ]}
      benefits={[
        { title: "Saúde Mental", description: "Evite o esgotamento físico e mental com uma rotina de trabalho equilibrada." },
        { title: "Foco Estratégico", description: "Tenha blocos de tempo dedicados apenas para prospectar novos clientes e parcerias." },
        { title: "Clientes Educados", description: "Crie uma relação de respeito onde o cliente valoriza o seu tempo e o seu suporte." },
        { title: "Eficiência Máxima", description: "Resolva mais problemas em menos tempo com o uso de tutoriais e manuais." }
      ]}
      howItWorks={[
        { title: "Mapeamento", description: "Identifique os horários de maior pico de mensagens dos seus clientes." },
        { title: "Definição", description: "Escolha seus turnos de atendimento e os canais oficiais de comunicação." },
        { title: "Comunicação", description: "Avise sua base sobre os novos horários e os processos de suporte." },
        { title: "Execução", description: "Mantenha a disciplina de seguir a rotina e utilize as ferramentas de automação." }
      ]}
      diferenciais={[
        { title: "Suporte Master 24/7", description: "Nossa equipe técnica cuida do servidor enquanto você foca no atendimento ao cliente." },
        { title: "Estabilidade Real", description: "Menos travamentos significam menos mensagens de suporte e mais paz na sua rotina." },
        { title: "Materiais de Apoio", description: "Fornecemos artes e tutoriais prontos para você enviar aos seus clientes." }
      ]}
      faq={[
        { question: "E se o cliente reclamar dos horários de atendimento?", answer: "Explique que a rotina permite que você entregue um suporte de mais qualidade quando está online. Clientes sérios entendem e valorizam isso." },
        { question: "O que fazer em emergências fora do horário?", answer: "Tenha um canal de status automático ou um bot que identifique palavras-chave de 'queda' e informe se é algo geral ou individual." },
        { question: "Como lidar com clientes impacientes?", answer: "Mantenha o profissionalismo, utilize respostas rápidas e sempre forneça uma estimativa de tempo para a solução do problema." }
      ]}
      breadcrumbs={breadcrumbs}
      sections={sections}
      ctaTitle="🚀 Pronto para Ter um Negócio Organizado e Lucrativo?"
      ctaDescription="Junte-se à P2 PRO e descubra como nossa estabilidade pode transformar sua rotina de atendimento."
      ctaButtonText="Ser um Revendedor Master"
    />
  );
};

export const Route = createFileRoute("/blog/como-criar-rotina-atendimento-clientes-iptv")({
  head: () => ({
    meta: [
      { title: "Como Criar uma Rotina de Atendimento para Clientes IPTV | P2 PRO" },
      { name: "description", content: "Aprenda a criar uma rotina de atendimento eficiente para sua revenda IPTV. Guia sobre gestão de tempo, suporte automatizado e produtividade." },
      { name: "keywords", content: "atendimento iptv, rotina revendedor iptv, suporte streaming, produtividade revenda" }
    ],
  }),
  component: ArticlePage,
});
